import {
  Button,
  Copy,
  Flex,
  Heading,
  Loader,
  Square,
  Stack,
} from '@grubhubprod/gh-cookbook-react';
import React, { ReactNode } from 'react';
import { useHistory } from 'react-router-dom';
import {
  SignupStep,
  STEP_MAPPING,
} from 'src/components/selfService/features/Signup/context/SignupStatusProvider/SignupStatusProvider';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';

const ROUTE_CHANGE_ANALYTICS_DELAY = 500;
interface IConfirmationStepCardContainerProps {
  isLoadingData?: boolean;
  hasError?: boolean;
  reloadData: () => void;
  stepName: string;
  stepNumber?: number;
  linkText?: string;
  children: ReactNode;
  useSecondaryText?: boolean;
}

export function ConfirmationStepCardContainer({
  isLoadingData,
  hasError,
  reloadData,
  stepName,
  stepNumber,
  linkText,
  children,
  useSecondaryText,
}: IConfirmationStepCardContainerProps): JSX.Element {
  const { sendTealiumEvent } = useAnalytics();
  const { push } = useHistory();

  function sendAnalytics() {
    const currentStep = STEP_MAPPING[stepNumber];
    switch (currentStep) {
      case SignupStep.COMPANY: {
        sendTealiumEvent({
          eventcategory: 'self-service sign up_review',
          eventaction: 'edit business details_cta',
          eventlabel: '',
        });
        break;
      }
      case SignupStep.VALIDATION: {
        sendTealiumEvent({
          eventcategory: 'self-service sign up_review',
          eventaction: 'edit financial information_cta',
          eventlabel: '',
        });
        break;
      }
      case SignupStep.BUDGET: {
        sendTealiumEvent({
          eventcategory: 'self-service sign up_review',
          eventaction: 'edit line of credit_cta',
          eventlabel: '',
        });
        break;
      }
      case SignupStep.PEOPLE: {
        sendTealiumEvent({
          eventcategory: 'self-service sign up_review',
          eventaction: 'edit people_cta',
          eventlabel: '',
        });
        break;
      }
      default: {
        break;
      }
    }
  }

  function goToEditStepPage(stepNumber: number) {
    // Need to dealay the route changing to ensure analytics event is fired from the current page
    sendAnalytics();
    setTimeout(
      () => push(`/corporate/signup/step/${stepNumber}?isUpdating=true`),
      ROUTE_CHANGE_ANALYTICS_DELAY
    );
  }

  return (
    <Square
      inset="md"
      borderRadius={8}
      dimension="1"
      position="relative"
      overflow="hidden"
    >
      {(isLoadingData || hasError) && (
        <Flex
          backgroundColor={hasError ? 'globals.white' : 'globals.white75a'}
          justifyContent="center"
          alignItems="center"
          position="absolute"
          top={0}
          bottom={0}
          left={0}
          right={0}
        >
          {isLoadingData && <Loader size="md" />}
          {hasError && (
            <>
              <Stack x="xxx-sm">
                <Copy variant="body">Something went wrong.</Copy>
              </Stack>
              <Button
                variant="tertiary"
                type="button"
                className="removeButtonPadding"
                onClick={reloadData}
              >
                <Copy variant="body" color="interactive">
                  Try again
                </Copy>
              </Button>
            </>
          )}
        </Flex>
      )}

      <Stack y="x-sm">
        <Flex justifyContent="space-between">
          <Heading
            variant="h4"
            color={useSecondaryText ? 'secondaryText' : 'primaryText'}
          >
            {stepName}
          </Heading>
          {linkText && stepNumber && (
            <Button
              className="removeButtonPadding"
              type="button"
              onClick={() => goToEditStepPage(stepNumber)}
              variant="tertiary"
              buttonSize="medium"
            >
              <Flex height="100%" alignItems="top">
                {linkText}
              </Flex>
            </Button>
          )}
        </Flex>
      </Stack>
      {children}
    </Square>
  );
}
